'use strict';

/*
  Images carousel without native scroll.

  Safari (mainly iPhone) creates a graphic layer for every scroll container
  (overflow auto/scroll) with the whole scrollable content painted, and it
  doesn't release it. With one carousel per product tile, the PLP crashes
  after some infinite scroll loads. Here the wrapper is overflow hidden and the
  content is moved with a 2D transform: Safari only creates a separate layer
  while the transition is running, and the rest of the time the carousel is
  painted in the page layer like any other image.

  Images: the slides (except the first one) are not lazysizes images. They are
  only loaded when the user interacts with the carousel (current slide and its
  neighbours), and they are unloaded (back to the placeholder) when the
  carousel goes far from the viewport: Safari keeps the decoded bitmap of every
  image referenced by an <img> (~2 MB for a w640 photo), even if it's not
  visible.
*/

// Selectors
const selector = {
  imagesCarousel: {
    wrapper: '.js-images-carousel',
    content: '.js-images-carousel__content',
    item: '.js-images-carousel__item',
    image: '.js-images-carousel__image',
    plpFirstItem: '.hw-plp-list__item--product:first-child',
    button: {
      prev: '.js-images-carousel__button-prev',
      next: '.js-images-carousel__button-next'
    }
  }
};

// Variables
const placeholderImage =
  "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' style='background:whitesmoke' viewBox='0 0 96 122'></svg>";
// Distance from the viewport to unload the images of a carousel
const unloadRootMargin = '100% 0px';
const transitionDuration = 300;
const transition = `transform ${transitionDuration}ms cubic-bezier(0.25, 1, 0.5, 1)`;
// Minimum movement (px) to decide the direction of a gesture. Small, because
// iOS starts the page scroll soon and then the gesture can't be cancelled
const dragThreshold = 6;
// Part of the item width to drag to change the slide
const dragChangeRatio = 0.2;
// Minimum speed (px/ms) of a flick to change the slide
const flickVelocity = 0.25;
// Time (ms) of the last part of the gesture used to calculate the speed
const velocitySampleTime = 100;
// Resistance when dragging beyond the first or the last slide
const edgeResistance = 0.3;
// Minimum horizontal trackpad movement to change the slide
const wheelThreshold = 30;
// Time without wheel events to consider the trackpad gesture finished
const wheelGestureEnd = 200;
// Time after a drag in which the click is ignored (the carousel is in a link)
const clickAfterDragDelay = 400;
// State of the gestures of each carousel (WeakMap to not retain removed
// tiles). The position is not here but in the DOM (see getIndex)
const carouselsState = new WeakMap();
let resizeListenerAdded = false;
let visibilityObserver = null;
let resizeTimeout = null;

// Functions

/**
 * Get the current index of the carousel. The position is stored in the DOM,
 * not in this module: the module is bundled in several entry points (main,
 * search, product-tile...) and each copy has its own variables, so a position
 * in memory would not be shared (e.g. product-tile resets the carousel when
 * the color variant changes)
 * @param {HTMLElement} carousel - Carousel element
 * @returns {number} Current index
 */
const getIndex = (carousel) =>
  parseInt(carousel.dataset.carouselIndex || '0', 10) || 0;

/**
 * Get the current offset (px) of the carousel content
 * @param {HTMLElement} carousel - Carousel element
 * @returns {number} Current offset
 */
const getOffset = (carousel) =>
  parseFloat(carousel.dataset.carouselOffset || '0') || 0;

/**
 * Get the gestures state of a carousel. Only the copy of the module that
 * initialized the carousel listens its gestures, so it can live in memory
 * @param {HTMLElement} carousel - Carousel element
 * @returns {Object} Gestures state
 */
const getState = (carousel) => {
  let state = carouselsState.get(carousel);

  if (!state) {
    state = {
      gesture: null,
      lastDragEnd: 0,
      wheel: { delta: 0, locked: false, timeout: null }
    };
    carouselsState.set(carousel, state);
  }

  return state;
};

/**
 * Get the content element (the one that is moved)
 * @param {HTMLElement} carousel - Carousel element
 * @returns {HTMLElement|null} Content element
 */
const getContent = (carousel) =>
  carousel.querySelector(selector.imagesCarousel.content);

/**
 * Get the items of the carousel
 * @param {HTMLElement} carousel - Carousel element
 * @returns {NodeList} Items
 */
const getItems = (carousel) =>
  carousel.querySelectorAll(selector.imagesCarousel.item);

/**
 * Get the width of one item
 * @param {HTMLElement} carousel - Carousel element
 * @returns {number} Item width
 */
const getItemWidth = (carousel) => {
  const item = carousel.querySelector(selector.imagesCarousel.item);

  return item ? item.offsetWidth : carousel.clientWidth;
};

/**
 * Get the maximum offset, so the last item is aligned to the right edge when
 * several items are visible (collections landing)
 * @param {HTMLElement} carousel - Carousel element
 * @returns {number} Maximum offset in px
 */
const getMaxOffset = (carousel) => {
  const content = getContent(carousel);

  return content ? Math.max(0, content.scrollWidth - carousel.clientWidth) : 0;
};

/**
 * Get the last index that can be the first visible item
 * @param {HTMLElement} carousel - Carousel element
 * @returns {number} Maximum index
 */
const getMaxIndex = (carousel) => {
  const itemWidth = getItemWidth(carousel) || 1;

  return Math.ceil(getMaxOffset(carousel) / itemWidth);
};

/**
 * Move the content of the carousel. A 2D transform is used: it only gets a
 * separate graphic layer while it's being animated
 * @param {HTMLElement} carousel - Carousel element
 * @param {number} offset - Offset in px
 * @param {boolean} animate - If the movement is animated
 * @returns {void}
 */
const setOffset = (carousel, offset, animate) => {
  const content = getContent(carousel);

  if (!content) {
    return;
  }

  carousel.dataset.carouselOffset = offset;
  content.style.transition = animate ? transition : 'none';
  content.style.transform = offset ? `translateX(${-offset}px)` : '';
};

/**
 * Observe the carousel to unload its images when it goes far from the
 * viewport. The observer is created when the first carousel loads images
 * @param {HTMLElement} carousel - Carousel element
 * @returns {void}
 */
const observeCarousel = (carousel) => {
  if (!('IntersectionObserver' in window)) {
    return;
  }

  if (!visibilityObserver) {
    visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            unloadCarousel(entry.target);
          }
        });
      },
      { rootMargin: unloadRootMargin }
    );
  }

  visibilityObserver.observe(carousel);
};

/**
 * Load the images of the items between two indexes
 * @param {HTMLElement} carousel - Carousel element
 * @param {number} fromIndex - First index to load
 * @param {number} toIndex - Last index to load
 * @returns {void}
 */
const loadImages = (carousel, fromIndex, toIndex) => {
  const items = getItems(carousel);
  let loaded = false;

  for (let index = Math.max(0, fromIndex); index <= toIndex; index++) {
    const item = items[index];

    if (item) {
      item.querySelectorAll(selector.imagesCarousel.image).forEach((image) => {
        if (
          image.dataset.src &&
          image.getAttribute('src') !== image.dataset.src
        ) {
          image.src = image.dataset.src;
          loaded = true;
        }
      });
    }
  }

  if (loaded) {
    observeCarousel(carousel);
  }
};

/**
 * Load the images of the visible items and their neighbours, so they are
 * ready when the user moves the carousel
 * @param {HTMLElement} carousel - Carousel element
 * @returns {void}
 */
const preloadImages = (carousel) => {
  const index = getIndex(carousel);

  loadImages(carousel, index - 1, index + getVisibleItems(carousel));
};

/**
 * Get the number of items visible at the same time
 * @param {HTMLElement} carousel - Carousel element
 * @returns {number} Visible items
 */
const getVisibleItems = (carousel) =>
  Math.max(1, Math.round(carousel.clientWidth / (getItemWidth(carousel) || 1)));

/**
 * Go to an item
 * @param {HTMLElement} carousel - Carousel element
 * @param {number} index - Index of the item
 * @param {boolean} animate - If the movement is animated
 * @returns {void}
 */
const goToIndex = (carousel, index, animate = true) => {
  const newIndex = Math.min(Math.max(index, 0), getMaxIndex(carousel));
  const offset = Math.min(
    newIndex * getItemWidth(carousel),
    getMaxOffset(carousel)
  );

  carousel.dataset.carouselIndex = newIndex;
  loadImages(carousel, newIndex, newIndex + getVisibleItems(carousel) - 1);
  setOffset(carousel, offset, animate);
};

/**
 * Navigate to the previous or next item
 * @param {HTMLElement} carousel - Carousel element
 * @param {String} direction - 'prev' or 'next'
 * @returns {void}
 */
const navigateTo = (carousel, direction) => {
  goToIndex(carousel, getIndex(carousel) + (direction === 'prev' ? -1 : 1));
  preloadImages(carousel);
};

/**
 * Go back to the first item without animation (used when the color variant of
 * the product tile changes)
 * @param {HTMLElement} carousel - Carousel element
 * @returns {void}
 */
const resetImagesCarousel = (carousel) => {
  if (carousel) {
    goToIndex(carousel, 0, false);
  }
};

/**
 * Go back to the first item and unload the images of the hidden items, so
 * Safari can release their decoded bitmaps. They are loaded again (from the
 * HTTP cache) if the user interacts with the carousel again
 * @param {HTMLElement} carousel - Carousel element
 * @returns {void}
 */
const unloadCarousel = (carousel) => {
  const visibleItems = getVisibleItems(carousel);

  goToIndex(carousel, 0, false);

  getItems(carousel).forEach((item, index) => {
    if (index < visibleItems) {
      return;
    }

    item.querySelectorAll(selector.imagesCarousel.image).forEach((image) => {
      if (image.getAttribute('src') !== placeholderImage) {
        image.src = placeholderImage;
      }
    });
  });

  if (visibilityObserver) {
    visibilityObserver.unobserve(carousel);
  }
};

/**
 * Listen the navigation buttons
 * @param {HTMLElement} carousel - Carousel element
 * @returns {void}
 */
const listenNavigationButtons = (carousel) => {
  const buttons = [
    [selector.imagesCarousel.button.prev, 'prev'],
    [selector.imagesCarousel.button.next, 'next']
  ];

  buttons.forEach(([buttonSelector, direction]) => {
    const button = carousel.querySelector(buttonSelector);

    if (button) {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        navigateTo(carousel, direction);
      });
    }
  });
};

/**
 * Give the content its own graphic layer while it's being dragged, so moving
 * it doesn't repaint the tile on every frame. The layer is removed when the
 * carousel stops: Safari keeps the memory of the layers that stay alive
 * @param {HTMLElement} carousel - Carousel element
 * @param {boolean} enabled - If the layer is enabled
 * @returns {void}
 */
const setDragLayer = (carousel, enabled) => {
  const content = getContent(carousel);

  if (!content) {
    return;
  }

  if (enabled) {
    content.dataset.dragLayer = 'active';
    content.style.willChange = 'transform';
    return;
  }

  // Remove it when the animation to the final slide ends, unless a new drag
  // has started in the meantime
  content.dataset.dragLayer = 'releasing';

  const release = () => {
    if (content.dataset.dragLayer === 'releasing') {
      delete content.dataset.dragLayer;
      content.style.willChange = '';
    }
  };

  content.addEventListener('transitionend', release, { once: true });
  setTimeout(release, transitionDuration + 100);
};

/**
 * Get the speed (px/ms) of the last part of the gesture. Using the whole
 * gesture, a flick after holding the finger would be too slow
 * @param {Array<Object>} samples - Last positions of the gesture
 * @returns {number} Speed (negative to the left)
 */
const getVelocity = (samples) => {
  if (samples.length < 2) {
    return 0;
  }

  const first = samples[0];
  const last = samples[samples.length - 1];

  return (last.x - first.x) / Math.max(1, last.time - first.time);
};

/**
 * Listen the drag gesture with touch events. The direction is decided with
 * the first movement: if it's horizontal, the touchmove is cancelled so the
 * page doesn't scroll in diagonal gestures (with pointer events and
 * touch-action, iOS scrolls the page with the vertical part of the gesture and
 * cancels the drag). If it's vertical, the rest of the gesture is ignored and
 * the page scroll keeps being native
 * @param {HTMLElement} carousel - Carousel element
 * @returns {void}
 */
const listenDrag = (carousel) => {
  const state = getState(carousel);

  const endDrag = (cancelled) => {
    const { gesture } = state;

    state.gesture = null;

    if (!gesture || !gesture.dragging) {
      return;
    }

    const deltaX = gesture.lastX - gesture.startX;
    const velocity = getVelocity(gesture.samples);
    const itemWidth = getItemWidth(carousel);
    let direction = 0;

    if (!cancelled) {
      if (Math.abs(velocity) > flickVelocity) {
        // Flick: the direction of the last movement
        direction = velocity < 0 ? 1 : -1;
      } else if (Math.abs(deltaX) > itemWidth * dragChangeRatio) {
        direction = deltaX < 0 ? 1 : -1;
      }
    }

    state.lastDragEnd = Date.now();
    goToIndex(carousel, getIndex(carousel) + direction);
    setDragLayer(carousel, false);
    preloadImages(carousel);
  };

  carousel.addEventListener(
    'touchstart',
    (event) => {
      if (event.touches.length !== 1) {
        // Pinch zoom: it's not a drag of the carousel
        if (state.gesture && state.gesture.dragging) {
          endDrag(true);
        }

        state.gesture = null;
        return;
      }

      const touch = event.touches[0];

      // The user touches the carousel: load the next images before the drag
      preloadImages(carousel);

      state.gesture = {
        startX: touch.clientX,
        startY: touch.clientY,
        lastX: touch.clientX,
        startOffset: getOffset(carousel),
        samples: [{ x: touch.clientX, time: event.timeStamp }],
        dragging: false
      };
    },
    { passive: true }
  );

  carousel.addEventListener(
    'touchmove',
    (event) => {
      const { gesture } = state;

      if (!gesture || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;

      if (!gesture.dragging) {
        if (
          Math.abs(deltaX) < dragThreshold &&
          Math.abs(deltaY) < dragThreshold
        ) {
          return;
        }

        // Vertical gesture, or the browser has already started the page
        // scroll (the event can't be cancelled): it's not a drag
        if (Math.abs(deltaX) <= Math.abs(deltaY) || !event.cancelable) {
          state.gesture = null;
          return;
        }

        gesture.dragging = true;
        // Start from the current position, without the jump of the threshold
        gesture.startX = touch.clientX;
        setDragLayer(carousel, true);

        // Stop the hint animation of the first PLP tile if it's running
        const content = getContent(carousel);

        if (content) {
          content.style.animation = 'none';
        }
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      gesture.lastX = touch.clientX;
      gesture.samples.push({ x: touch.clientX, time: event.timeStamp });

      while (
        gesture.samples.length > 2 &&
        event.timeStamp - gesture.samples[0].time > velocitySampleTime
      ) {
        gesture.samples.shift();
      }

      const maxOffset = getMaxOffset(carousel);
      let offset = gesture.startOffset - (touch.clientX - gesture.startX);

      if (offset < 0) {
        offset *= edgeResistance;
      } else if (offset > maxOffset) {
        offset = maxOffset + (offset - maxOffset) * edgeResistance;
      }

      setOffset(carousel, offset, false);
    },
    { passive: false }
  );

  carousel.addEventListener('touchend', () => endDrag(false), {
    passive: true
  });
  carousel.addEventListener('touchcancel', () => endDrag(true), {
    passive: true
  });

  // Mouse: the navigation buttons appear on hover, load the next images
  carousel.addEventListener('pointerenter', (event) => {
    if (event.pointerType === 'mouse') {
      preloadImages(carousel);
    }
  });

  // The carousel is inside the product link: a drag must not open the PDP
  carousel.addEventListener(
    'click',
    (event) => {
      if (Date.now() - state.lastDragEnd < clickAfterDragDelay) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );
};

/**
 * Listen the horizontal trackpad gesture. One gesture moves one item. It's
 * not passive to avoid the history navigation of the browser with the
 * horizontal swipe
 * @param {HTMLElement} carousel - Carousel element
 * @returns {void}
 */
const listenWheel = (carousel) => {
  const { wheel } = getState(carousel);

  carousel.addEventListener(
    'wheel',
    (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
        return;
      }

      event.preventDefault();

      clearTimeout(wheel.timeout);
      wheel.timeout = setTimeout(() => {
        wheel.delta = 0;
        wheel.locked = false;
      }, wheelGestureEnd);

      if (wheel.locked) {
        return;
      }

      wheel.delta += event.deltaX;

      if (Math.abs(wheel.delta) >= wheelThreshold) {
        wheel.locked = true;
        navigateTo(carousel, wheel.delta > 0 ? 'next' : 'prev');
      }
    },
    { passive: false }
  );
};

/**
 * Recalculate the offset of the moved carousels when the viewport changes,
 * because it's in px
 * @returns {void}
 */
const listenResize = () => {
  if (resizeListenerAdded) {
    return;
  }

  resizeListenerAdded = true;

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      document
        .querySelectorAll(selector.imagesCarousel.wrapper)
        .forEach((carousel) => {
          const index = getIndex(carousel);

          if (index > 0) {
            goToIndex(carousel, index, false);
          }
        });
    }, 150);
  });
};

/**
 * Listen images carousel elements. It can be called several times (infinite
 * scroll, sorting) and from several copies of this module: each carousel is
 * initialized once, marked in the DOM
 * @returns {void}
 */
const listenImagesCarousel = () => {
  listenResize();

  document
    .querySelectorAll(selector.imagesCarousel.wrapper)
    .forEach((carousel) => {
      if (carousel.dataset.carouselInitialized) {
        return;
      }

      carousel.dataset.carouselInitialized = 'true';

      // The hint animation of the first PLP tile (touch devices) shows part
      // of the second slide
      if (
        carousel.closest(selector.imagesCarousel.plpFirstItem) &&
        window.matchMedia('(hover: none) and (pointer: coarse)').matches
      ) {
        loadImages(carousel, 1, 1);
      }

      listenNavigationButtons(carousel);
      listenDrag(carousel);
      listenWheel(carousel);
    });
};

// Export
export { listenImagesCarousel, resetImagesCarousel };
